import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ApplyMentorModal from '../components/ApplyMentorModal';
import { downloadMentorGuide } from '../utils/downloadHelpers';
import { 
  Users, 
  Target, 
  Award, 
  BookOpen,
  Building2,
  Lightbulb,
  Download
} from 'lucide-react';
import { 
  mentorMetrics, 
  mentorRoles, 
  leadingOrganizations,
  industryCategories 
} from '../mockPages';

const ForMentors = () => {
  const roleIcons = [BookOpen, Users, Award, Lightbulb];
  const [showMentorModal, setShowMentorModal] = useState(false);

  return (
    <div className="min-h-screen" style={{backgroundColor: '#fffaf1'}}>
      <Helmet>
        <title>Become a Mentor - Shape Industry-Ready Talent | EduSocial</title>
        <meta name="description" content="Join 250+ mentors from TCS, Infosys, Google, and other leading companies. Guide students through workshops, masterclasses, and project mentorship." />
        <meta property="og:title" content="Become a Mentor - Shape Industry-Ready Talent | EduSocial" />
        <meta property="og:description" content="Share your expertise with 10K+ students across India through EduSocial's mentor network." />
        <link rel="canonical" href="https://edusocial.co.in/for-mentors" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(135deg, #fffaf1 0%, #fff5e1 100%)'}}>
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{color: '#012726'}}>
            Shape the Next Generation of{' '}
            <span style={{color: '#33c375'}}>Industry-Ready Talent</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Join EduSocial's Centre of Excellence network as a mentor. Your industry experience will 
            directly influence curriculum relevance and student readiness across leading colleges.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-lg"
              style={{backgroundColor: '#33c375'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2ab066'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#33c375'}
              onClick={() => setShowMentorModal(true)}
            >
              Become a Mentor
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="px-8 py-6 text-lg transition-all duration-200"
              style={{borderColor: '#012726', borderWidth: '2px', color: '#012726'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f9f4'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              onClick={downloadMentorGuide}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {mentorMetrics.map((metric, index) => (
              <div 
                key={index} 
                className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
                style={{border: '2px solid #e8f5ee'}}
              >
                <div className="text-4xl sm:text-5xl font-bold mb-2" style={{color: '#33c375'}}>
                  {metric.number}
                </div>
                <div className="text-gray-600 font-medium">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Role As A Mentor */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Your Role As A Mentor
            </h2>
            <h3 className="text-xl sm:text-2xl font-semibold mb-4" style={{color: '#33c375'}}>
              What It Means for You
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Become part of a Centre of Excellence network where your experience directly shapes industry-ready talent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mentorRoles.map((role, index) => {
              const Icon = roleIcons[index];
              return (
                <Card 
                  key={role.id} 
                  className="border-2 hover:shadow-lg transition-all duration-300 bg-white"
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#33c375'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = ''}
                >
                  <CardContent className="p-6 space-y-4">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto"
                      style={{backgroundColor: '#33c375'}}
                    >
                      <Icon className="text-white" size={28} />
                    </div>
                    <h4 className="text-lg font-semibold text-center" style={{color: '#012726'}}>
                      {role.title}
                    </h4>
                    <p className="text-gray-600 text-center text-sm leading-relaxed">
                      {role.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trusted Network */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Our Mentors Come From Leading Organizations
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Industry professionals from top companies across Technology, Finance, Healthcare, Manufacturing, and more
            </p>
          </div>

          {/* Company Logos Grid */}
          <div className="flex flex-wrap justify-center items-center gap-6 mb-8">
            {leadingOrganizations.map((org, index) => (
              <div 
                key={index} 
                className="px-6 py-3 bg-white border-2 border-gray-200 rounded-lg hover:shadow-md transition-all duration-200"
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#33c375'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <span className="text-gray-700 font-semibold">{org}</span>
              </div>
            ))}
            <Badge 
              className="px-4 py-2 text-sm"
              style={{backgroundColor: '#e8f5ee', color: '#012726', borderColor: '#33c375'}}
            >
              +50 More
            </Badge>
          </div>

          {/* Industry Categories */}
          <div className="mt-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industryCategories.map((industry) => (
                <Card key={industry.id} className="border-2 bg-white hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-bold" style={{color: '#012726'}}>
                      {industry.category}
                    </h3>
                    <div className="space-y-2">
                      {industry.roles.map((role, idx) => (
                        <p key={idx} className="text-gray-700 text-sm">
                          • {role}
                        </p>
                      ))}
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-xs font-semibold" style={{color: '#33c375'}}>
                        FROM: {industry.companies}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg font-semibold" style={{color: '#012726'}}>
              Join 100+ verified industry professionals from 50+ leading organizations
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 text-white" style={{background: 'linear-gradient(135deg, #012726 0%, #014d49 100%)'}}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Shape the Future of Talent?
          </h2>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8">
            Join 250+ industry professionals already mentoring through EduSocial's Centre of Excellence network.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-lg"
              style={{backgroundColor: '#33c375'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2ab066'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#33c375'}
              onClick={() => setShowMentorModal(true)}
            >
              Apply as a Mentor
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 text-white px-8 py-6 text-lg transition-all duration-200"
              style={{borderColor: 'white'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              onClick={downloadMentorGuide}
            >
              <Download className="mr-2" size={20} />
              Download Mentor Guide
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Badge className="px-4 py-2 text-sm" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)'}}>
              Flexible commitment
            </Badge>
            <Badge className="px-4 py-2 text-sm" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)'}}>
              Virtual & in-person
            </Badge>
            <Badge className="px-4 py-2 text-sm" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)'}}>
              Full support provided
            </Badge>
          </div>
        </div>
      </section>
      
      <ApplyMentorModal open={showMentorModal} onOpenChange={setShowMentorModal} />
    </div>
  );
};

export default ForMentors;
